import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { bannerAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const BANNER_TYPES = {
  HOME: 'home',
  GAME: 'game',
  PROMOTION: 'promotion',
  GENERAL: 'general',
};

const BANNER_TYPE_LABELS = {
  home: 'Home',
  game: 'Game',
  promotion: 'Promotion',
  general: 'General',
};

const BANNER_TYPE_COLORS = {
  home: 'primary',
  game: 'secondary',
  promotion: 'warning',
  general: 'default',
};

const EMPTY_FORM = {
  title: '',
  link_url: '',
  banner_type: BANNER_TYPES.GENERAL,
  sort_order: 0,
  is_active: true,
  valid_until: '',
};

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await bannerAPI.getBanners({ include_inactive: true });
      if (response.data.success) {
        setBanners(response.data.data);
      }
    } catch {
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingBanner(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData(EMPTY_FORM);
    setOpenDialog(true);
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setImageFile(null);
    setImagePreview(banner.image_url || null);
    setFormData({
      title: banner.title || '',
      link_url: banner.link_url || '',
      banner_type: banner.banner_type || BANNER_TYPES.GENERAL,
      sort_order: banner.sort_order ?? 0,
      is_active: banner.is_active,
      valid_until: banner.valid_until
        ? new Date(banner.valid_until).toISOString().slice(0, 16)
        : '',
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only image files (jpeg, jpg, png, gif, webp) are allowed!');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(editingBanner?.image_url || null);
  };

  const handleSubmit = async () => {
    if (!editingBanner && !imageFile) {
      toast.error('Banner image is required');
      return;
    }

    try {
      setLoading(true);
      const payload = new FormData();

      if (imageFile) payload.append('image', imageFile);
      if (formData.title.trim()) payload.append('title', formData.title.trim());
      if (formData.link_url.trim()) payload.append('link_url', formData.link_url.trim());
      payload.append('banner_type', formData.banner_type);
      payload.append('sort_order', formData.sort_order);
      payload.append('is_active', formData.is_active);
      if (formData.valid_until) payload.append('valid_until', new Date(formData.valid_until).toISOString());

      if (editingBanner) {
        const response = await bannerAPI.updateBanner(editingBanner.id, payload);
        if (response.data.success) toast.success('Banner updated successfully');
      } else {
        const response = await bannerAPI.createBanner(payload);
        if (response.data.success) toast.success('Banner created successfully');
      }

      closeDialog();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      setLoading(true);
      const response = await bannerAPI.deleteBanner(id);
      if (response.data.success) {
        toast.success('Banner deleted successfully');
        fetchBanners();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      setLoading(true);
      const response = await bannerAPI.toggleBannerStatus(id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBanners();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Toggle failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const isSubmitDisabled =
    loading || (!editingBanner && !imageFile);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Banner Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          disabled={loading}
        >
          Add Banner
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Preview</TableCell>
                  <TableCell>Title & Link</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Sort</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {banners.map((banner) => (
                  <TableRow key={banner.id}>
                    <TableCell>
                      {banner.image_url ? (
                        <Box
                          component="img"
                          src={banner.image_url}
                          alt={banner.title || 'Banner'}
                          sx={{
                            width: 100,
                            height: 56,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 100,
                            height: 56,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.default',
                          }}
                        >
                          <ImageIcon color="disabled" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {banner.title || <em style={{ color: '#9e9e9e' }}>No title</em>}
                      </Typography>
                      {banner.link_url && (
                        <Tooltip title={banner.link_url}>
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => window.open(banner.link_url, '_blank', 'noopener,noreferrer')}
                          >
                            <OpenInNewIcon sx={{ fontSize: 12 }} />
                            {banner.link_url}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={BANNER_TYPE_LABELS[banner.banner_type] || banner.banner_type}
                        color={BANNER_TYPE_COLORS[banner.banner_type] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{banner.sort_order ?? 0}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(banner.valid_until)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={banner.is_active ? 'Active' : 'Inactive'}
                        color={banner.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(banner)}
                            disabled={loading}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={banner.is_active ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            color={banner.is_active ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(banner.id)}
                            disabled={loading}
                          >
                            {banner.is_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(banner.id)}
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {banners.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No banners found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBanner ? 'Edit Banner' : 'Add Banner'}
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Image upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Banner Image {!editingBanner && <span style={{ color: 'red' }}>*</span>}
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                }}
              >
                {imagePreview ? (
                  <Box>
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 160,
                        objectFit: 'contain',
                        mb: 1,
                        borderRadius: 1,
                      }}
                    />
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                      >
                        Change
                        <input
                          type="file"
                          hidden
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleImageChange}
                        />
                      </Button>
                      {imageFile && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={handleRemoveImage}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Upload Banner Image (Max 5 MB)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Supported: JPEG, JPG, PNG, GIF, WEBP
                    </Typography>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Image
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageChange}
                      />
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Optional"
            />

            <TextField
              label="Link URL"
              fullWidth
              value={formData.link_url}
              onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              placeholder="https://... (optional)"
            />

            <Box display="flex" gap={2}>
              <TextField
                select
                label="Banner Type"
                fullWidth
                value={formData.banner_type}
                onChange={(e) => setFormData({ ...formData, banner_type: e.target.value })}
              >
                {Object.entries(BANNER_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Sort Order"
                type="number"
                fullWidth
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                inputProps={{ min: 0, step: 1 }}
              />
            </Box>

            <TextField
              label="Valid Until"
              type="datetime-local"
              fullWidth
              value={formData.valid_until}
              onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiry"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {editingBanner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BannerManagement;
